import { useLocalization } from "@fluent/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateCreditCardApplicationQuery } from "@lending/hooks/queries/CreditCardApplication/useCreateCreditCardApplicationQuery";
import { useGetCreditCardProductsQuery } from "@lending/hooks/queries/CreditCardProduct/useGetCreditCardProductsQuery";
import { useCreateEligibilitySelectionForApplication } from "@lending/hooks/queries/EligibilityCriteria/useCreateEligibilitySelectionForApplication";
import { useToastsContext } from "@lending/hooks/useToasts";
import { withMlaStatusMetadata } from "@lending/pages/Common/ApplicationQuestions/mlaQuestion";
import { PREFILL_DATA_SOURCE } from "@lending/types";
import { useStore } from "../../../../contexts/StoreContext";
import type { PersonalInfoApiData } from "@lending/hooks/queries/PersonalInfo/types";

function useCreateCreditCardApplication() {
  const { l10n } = useLocalization();
  const queryClient = useQueryClient();
  const { showErrorToast } = useToastsContext();
  const { data: storeData, updateData: updateStoreData } = useStore();
  const { creditCardProductsData } = useGetCreditCardProductsQuery();

  const createCreditCardApplication = useCreateCreditCardApplicationQuery();
  const { createEligibilitySelectionForApplication } =
    useCreateEligibilitySelectionForApplication();

  async function createApplication(data: PersonalInfoApiData, captcha?: string | null) {
    let selectedProduct = storeData.selectedProduct;
    if (!selectedProduct) {
      if (creditCardProductsData.count > 1) {
        showErrorToast(
          l10n.getString(
            "error-credit-card-product-selection",
            null,
            "You must select a credit card product."
          )
        );
        return null;
      }
      selectedProduct = creditCardProductsData.results[0].id;
    }

    await queryClient.invalidateQueries({ queryKey: ["csrfToken"] });

    const metadata = withMlaStatusMetadata(storeData.applicationMetadata, storeData.mlaStatus);
    const prefillDataSource = storeData.prefillDataSource ?? PREFILL_DATA_SOURCE.MANUAL;

    // The primary applicant is created atomically with the application, so an application is
    // never left without one; a co-applicant is added separately after this step.
    const application = await createCreditCardApplication.mutateAsync({
      // @ts-expect-error openapi-typescript does not respect readonly payload fields
      body: {
        product: selectedProduct,
        origin: "institution_site",
        primary_applicant: {
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          phone: data.phone,
          tax_id: data.tax_id,
          date_of_birth: data.date_of_birth,
          prefill_data_source: prefillDataSource,
        },
        ...(captcha ? { captcha } : {}),
        ...(metadata ? { metadata } : {}),
      },
    });

    updateStoreData({ lendingApplicationID: application.id });

    await createEligibilitySelectionForApplication(application.id);

    updateStoreData({
      tax_id: data.tax_id,
      date_of_birth: data.date_of_birth,
      prefillDataSource,
      skippedPlaidPrefill: prefillDataSource === PREFILL_DATA_SOURCE.MANUAL,
    });

    return { application, applicantDetails: application.primary_applicant };
  }

  return { createApplication };
}

export { useCreateCreditCardApplication };
